import React, { useState, useEffect, useMemo } from "react";
import { Table, Tag, Button, Tooltip } from "antd";
import {
  ExclamationCircleOutlined,
  WarningOutlined,
  CaretRightOutlined,
  CaretLeftOutlined,
} from "@ant-design/icons";

const IncompleteNodesSidebar = ({
  nodes,
  edges,
  onTestClick,
  onNodesUnfilled,
}) => {
  const [sidebarWidth, setSidebarWidth] = useState(160);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [incompleteNodes, setIncompleteNodes] = useState([]);

  const handleTestClick = (node) => {
    if (onTestClick) {
      onTestClick(node);
    }
  };

  const handleNodesUnfilled = (nodes) => {
    if (onNodesUnfilled) {
      onNodesUnfilled(nodes);
    }
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
    setSidebarWidth(isCollapsed ? 150 : 0);
  };

  const handleMouseDown = () => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!isCollapsed) {
      setSidebarWidth(e.clientX);
    }
  };

  const handleMouseUp = () => {
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const checkDates = (source, target) => {
    const parent = nodes.find((node) => node.id === source);
    const child = nodes.find((node) => node.id === target);

    if (!parent || !child) return false; // Ignore missing nodes
    if (!parent.data.endDate || !child.data.startDate) return false; // Ignore missing dates

    return child.data.startDate < parent.data.endDate; // Returns true if dates are incorrect
  };

  const getNodesWithInconsistentDates = () => {
    return edges
      .filter((edge) => checkDates(edge.source, edge.target))
      .map((edge) => nodes.find((node) => node.id === edge.target))
      .filter(Boolean); // Remove undefined entries
  };

  const getNodesWithInconsistentSamples = () => {
    let inconsistentSamplesNodes = [];

    nodes.forEach((parentNode) => {
      const childNodes = edges
        .filter((edge) => edge.source === parentNode.id)
        .map((edge) => nodes.find((node) => node.id === edge.target))
        .filter(Boolean); // Remove undefined entries

      if (childNodes.length > 0) {
        const totalChildSamplesUsed = childNodes.reduce(
          (sum, child) => sum + (child.data.samplesUsed || 0),
          0
        );

        if (totalChildSamplesUsed > (parentNode.data.samplesUsed || 0)) {
          inconsistentSamplesNodes.push(parentNode);
        }
      }
    });

    return inconsistentSamplesNodes;
  };

  const incompleteNodesMemo = useMemo(
    () =>
      nodes
        .filter(
          (node) =>
            (node.data.hoursEstimation === "" || node.data.expenseEstimation === "") && !node.data.testNumber.startsWith("Flow:")
        )
        .map((node, index) => ({
          key: index,
          testName: node.data.testName,
          testNumber: node.data.testNumber,
          hoursEstimation: node.data.hoursEstimation,
          node: node,
          warnings: ["Missing Details"],
        })),
    [nodes]
  );

  useEffect(() => {
    handleNodesUnfilled(incompleteNodesMemo);
    console.log(incompleteNodes)
  }, [incompleteNodesMemo]);

  useEffect(() => {
    const filteredNodes = incompleteNodesMemo.map((node) => node.node.id);

    const irregularDatesNodes = getNodesWithInconsistentDates();
    const inconsistentSamplesNodes = getNodesWithInconsistentSamples();

    const allWarnings = nodes.map((node) => {
      const warnings = [];

      if (filteredNodes.includes(node.id)) {
        warnings.push("Missing Details");
      }
      if (irregularDatesNodes.includes(node)) {
        warnings.push("Inconsistent Dates");
      }
      if (inconsistentSamplesNodes.includes(node)) {
        warnings.push("Inconsistent Samples");
      }

      return warnings.length > 0
        ? {
            key: node.id,
            testName: node.data.testName,
            testNumber: node.data.testNumber,
            hoursEstimation: node.data.hoursEstimation,
            node: node,
            warnings,
          }
        : null;
    });

    setIncompleteNodes(allWarnings.filter(Boolean)); // Remove null values
  }, [nodes, edges, incompleteNodesMemo]);

  const columns = [
    {
      dataIndex: "testNumber",
      key: "testNumber",
      render: (text, record) => {
        const warnings = record.warnings || [];
        return (
          <Tooltip
            title={warnings
              .map((warning) => {
                if (warning === "Inconsistent Dates")
                  return "The Start Date is before the End Date of parent test";
                if (warning === "Inconsistent Samples")
                  return "The sum of child test's used samples exceeds the parent node's samples";
                return "Please fill out the Expense and Hours Estimations";
              })
              .join("\n")}
          >
            {warnings.length > 0
              ? warnings.map((warning) => {
                  let warningIcon = <ExclamationCircleOutlined />;
                  let warningColor = "red";

                  if (warning === "Inconsistent Dates") {
                    warningIcon = <WarningOutlined />;
                    warningColor = "orange";
                  } else if (warning === "Inconsistent Samples") {
                    warningColor = "purple";
                  }

                  return (
                    <Tag color={warningColor} key={warning}>
                      {warningIcon} {warning}
                    </Tag>
                  );
                })
              : null}
            {text ? text.substring(0, 10) : null}
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div style={{ display: "flex", alignItems: "stretch", height: "89vh" }}>
      <div
        style={{
          position: "relative",
          width: sidebarWidth,
          background: "#f0f0f0",
          padding: "10px",
        }}
      >
        <div style={{ position: "absolute", top: "45%", right: -20 }}>
          <Button
            type="primary"
            onClick={toggleSidebar}
            style={{
              marginBottom: 16,
              background: "transparent",
              boxShadow: 0,
            }}
          >
            {isCollapsed ? (
              <CaretRightOutlined className="details-button" />
            ) : (
              <CaretLeftOutlined className="details-button" />
            )}
          </Button>
        </div>
        {!isCollapsed && (
          <div style={{ height: "85vh", overflowY: "auto" }}>
            <h4 style={{ marginTop: "5px" }}>Incomplete Tests</h4>
            <Table
              columns={columns}
              dataSource={incompleteNodes}
              pagination={false}
              size="small"
              rowClassName="incomplete-row"
              onRow={(record) => ({
                onClick: () => handleTestClick(record.node),
                onMouseEnter: (event) => {
                  event.currentTarget.style.cursor = "pointer";
                },
              })}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default IncompleteNodesSidebar;
